import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { EavEntityTypeFindManyQueryInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find-many-query.dto';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import { BasisCatalogEavEntityTypeService } from '@zaoblako/web/core/services/basis/basis-catalog-eav-entity-type.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-eav-entity-type-single-select',
  standalone: true,
  imports: [CommonModule, SpinnerComponent, SingleSelectComponent],
  templateUrl: './eav-entity-type-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EavEntityTypeSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public types: EavEntityTypeFindInterface[] = [];
  public queryParams: EavEntityTypeFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedType: EavEntityTypeFindInterface;
  @Input() public set selectedType(selectedType: EavEntityTypeFindInterface) {
    if (this.customSearch) {
      this._selectedType = selectedType;
      return;
    }

    if (this.types.some((type) => type.id === selectedType?.id)) {
      this._selectedType = this.types.find((type) => type.id === selectedType.id);
      return;
    }

    if (selectedType?.id) {
      if (typeof selectedType.id === 'string') {
        selectedType.id = +selectedType.id;
      }

      this.basisCatalogEavEntityTypeService.findById<EavEntityTypeFindInterface>(selectedType.id).subscribe({
        next: (_type) => {
          if (!this.types.some((type) => type.id === _type.id)) {
            this.types = this.types.concat(_type);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedType = _type;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
    }
  }

  @Output() public selectedTypeChange = new EventEmitter<EavEntityTypeFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedTypeId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.types.some((type) => type.id === _id)) {
      this._selectedType = this.types.find((type) => type.id === _id);
      return;
    }

    this.basisCatalogEavEntityTypeService.findById<EavEntityTypeFindInterface>(_id).subscribe({
      next: (_type) => {
        if (!this.types.some((type) => type.id === _type.id)) {
          this.types = this.types.concat(_type);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedType = _type;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly basisCatalogEavEntityTypeService: BasisCatalogEavEntityTypeService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.basisCatalogEavEntityTypeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.types = this.types?.length
            ? this.types.concat(data.items.filter((item) => !this.types.some((type) => type.id === item.id)))
            : data.items.filter((item) => !this.types.some((type) => type.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(type: EavEntityTypeFindInterface) {
    this.selectedTypeChange.emit(type);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.types.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.basisCatalogEavEntityTypeService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.types = this.types.concat(
                    data.items.filter((item) => !this.types.some((type) => type.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.basisCatalogEavEntityTypeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedType?.id) && this._selectedType) {
            this.types = data.items.concat(this._selectedType);
          } else {
            this.types = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
